import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { COLORS } from '../../constants/theme';
import { downloadPersonas, exportFormats } from '../../utils/exportUtils';
import PersonaCard from './PersonaCard';

const ResultHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const DataSourceBanner = styled.div`
  padding: 1rem 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-size: 0.95rem;
  line-height: 1.5;
  background: ${({ $type }) =>
    $type === 'real-api' ? 'rgba(46, 204, 113, 0.12)' :
    $type === 'synthetic' ? 'rgba(231, 76, 60, 0.12)' :
    'rgba(243, 156, 18, 0.12)'};
  border: 1px solid ${({ $type }) =>
    $type === 'real-api' ? 'rgba(46, 204, 113, 0.4)' :
    $type === 'synthetic' ? 'rgba(231, 76, 60, 0.4)' :
    'rgba(243, 156, 18, 0.4)'};
  color: ${({ $type }) =>
    $type === 'real-api' ? '#2ecc71' :
    $type === 'synthetic' ? '#e74c3c' :
    '#f39c12'};
`;

const BannerIcon = styled.span`
  font-size: 1.3rem;
  flex-shrink: 0;
  margin-top: 0.1rem;
`;

const BannerText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const BannerTitle = styled.strong`
  font-size: 1rem;
`;

const BannerDetail = styled.span`
  font-size: 0.85rem;
  opacity: 0.85;
`;

const DownloadSection = styled.div`
  margin: 2rem 0;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid ${COLORS.whiteSubtle};
`;

const DownloadTitle = styled.h3`
  color: ${COLORS.cyan};
  margin-bottom: 1rem;
  text-align: center;
`;

const PersonasGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  width: 100%;
  @media (max-width: 1200px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const DATA_SOURCE_CONFIG = {
  'real-api': { icon: '\u2705', key: 'data_source_real' },
  'ai-estimated': { icon: '\u26A0\uFE0F', key: 'data_source_ai_estimated' },
  'synthetic': { icon: '\u{1F9EA}', key: 'data_source_synthetic' },
  'survey': { icon: '\u{1F4CA}', key: 'data_source_survey' },
};

function PersonaResults({ result, personas }) {
  const { t } = useTranslation();

  if (!result || !personas || personas.length === 0) return null;

  const sourceType = result.data_source_type || (result.using_default_data ? 'ai-estimated' : 'real-api');
  const sourceConfig = DATA_SOURCE_CONFIG[sourceType] || DATA_SOURCE_CONFIG['ai-estimated'];

  return (
    <>
      <ResultHeader>
        <h2>{t('personas_heading')}</h2>
      </ResultHeader>

      <DataSourceBanner id="persona-datasource-banner-036" $type={sourceType}>
        <BannerIcon>{sourceConfig.icon}</BannerIcon>
        <BannerText>
          <BannerTitle>{t(sourceConfig.key)}</BannerTitle>
          {result.notes && <BannerDetail>{result.notes}</BannerDetail>}
        </BannerText>
      </DataSourceBanner>

      <DownloadSection>
        <DownloadTitle>{t('download.title')}</DownloadTitle>
        <div className="download-container">
          <button
            id="persona-download-json-028"
            className="download-button json"
            onClick={() => downloadPersonas(personas, exportFormats.JSON)}
            title={t('download.tooltip')}
          >
            {t('download.json')}
          </button>
          <button
            id="persona-download-csv-029"
            className="download-button csv"
            onClick={() => downloadPersonas(personas, exportFormats.CSV)}
            title={t('download.tooltip')}
          >
            {t('download.csv')}
          </button>
          <button
            id="persona-download-pdf-030"
            className="download-button pdf"
            onClick={() => downloadPersonas(personas, exportFormats.PDF)}
            title={t('download.tooltip')}
          >
            {t('download.pdf')}
          </button>
        </div>
      </DownloadSection>

      <PersonasGrid>
        {personas.map((persona, index) => (
          <PersonaCard key={index} persona={persona} />
        ))}
      </PersonasGrid>
    </>
  );
}

export default PersonaResults;
